import { useState } from 'react';
import { Upload, ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';

interface BannerUploadProps {
  currentBannerUrl?: string | null;
  onUploadSuccess: (bannerUrl: string) => void;
}

export function BannerUpload({ currentBannerUrl, onUploadSuccess }: BannerUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be smaller than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/uploads/banner', formData);
      const bannerUrl = (response as any).data?.bannerUrl;
      if (bannerUrl) {
        onUploadSuccess(bannerUrl);
        toast.success('Banner uploaded!');
      } else {
        throw new Error('No banner URL returned');
      }
    } catch (error) {
      toast.error('Failed to upload banner');
      setPreview(null);
    } finally {
      setIsUploading(false);
    }
  };

  const displayUrl = preview || currentBannerUrl;

  return (
    <div className="w-full">
      <div className="relative w-full h-36 rounded-lg overflow-hidden bg-leather border-2 border-dashed border-gold/30 group">
        {displayUrl ? (
          <>
            <img
              src={displayUrl}
              alt="Profile banner"
              className="w-full h-full object-cover"
            />
            {isUploading && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-gold border-t-transparent" />
              </div>
            )}
            {!isUploading && (
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-leather font-medium rounded-lg hover:bg-gold-bright transition-colors">
                    <Upload className="w-4 h-4" /> Change Banner
                  </span>
                </label>
              </div>
            )}
          </>
        ) : (
          <label className="cursor-pointer flex flex-col items-center justify-center w-full h-full gap-2">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
              disabled={isUploading}
            />
            {isUploading ? (
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gold border-t-transparent" />
            ) : (
              <>
                <ImageIcon className="w-8 h-8 text-gold/40" />
                <span className="text-text-muted text-sm">Click to upload profile banner</span>
              </>
            )}
          </label>
        )}
      </div>
      <p className="text-text-muted text-xs mt-1">
        Recommended: 1200×300px. JPG, PNG, GIF, or WebP (max 10MB)
      </p>
    </div>
  );
}
